import { useCallback } from "react";
import { auth } from "../../../firebase";
import { WorkspacePermissions, WorkspaceRole } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

interface UsePermissionHelpersProps {
  permissions: WorkspacePermissions | null;
  currentUserRole: WorkspaceRole;
  workspaceOwnerId?: string | null;
}

/** Normalize role for permission check (member -> user) */
function normalizeRole(role: WorkspaceRole): WorkspaceRole {
  return role === "member" ? "user" : role;
}

/** Helper: can user perform action given permission key and default roles when key missing */
function hasPermission(
  isPrimaryOwner: boolean,
  permissions: WorkspacePermissions | null,
  currentUserRole: WorkspaceRole,
  permissionKey: keyof Pick<WorkspacePermissions, "deleteTransactions" | "editTransactions" | "viewTransactionHistory" | "addCurrency" | "changeDefaultCurrency" | "changeCurrencyRateDecimalPlaces" | "editPreferences">,
  defaultRoles: WorkspaceRole[]
): boolean {
  if (isDemoMode() || isBypassAuthEnabled()) return true;
  if (isPrimaryOwner) return true;
  const allowed = permissions?.[permissionKey] ?? defaultRoles;
  if (!Array.isArray(allowed) || allowed.length === 0) return false;
  const role = normalizeRole(currentUserRole);
  return allowed.some((r) => normalizeRole(r as WorkspaceRole) === role);
}

/**
 * Hook to check user permissions for various actions.
 * Returns functions to check if user can perform specific actions.
 */
export const usePermissionHelpers = ({
  permissions,
  currentUserRole,
  workspaceOwnerId,
}: UsePermissionHelpersProps): {
  canDeleteTransactions: () => boolean;
  canEditTransactions: () => boolean;
  canViewTransactionHistory: () => boolean;
  canAddCurrency: () => boolean;
  canChangeDefaultCurrency: () => boolean;
  canChangeCurrencyRateDecimalPlaces: () => boolean;
  canEditPreferences: () => boolean;
} => {
  // Check if current user is primary owner
  const isPrimaryOwner = workspaceOwnerId != null && auth.currentUser?.uid === workspaceOwnerId;
  
  // Check if user can delete transactions based on permissions
  const canDeleteTransactions = useCallback((): boolean => {
    return hasPermission(isPrimaryOwner, permissions, currentUserRole, "deleteTransactions", ["owner"]);
  }, [permissions, currentUserRole, isPrimaryOwner]);

  const canEditTransactions = useCallback((): boolean => {
    return hasPermission(isPrimaryOwner, permissions, currentUserRole, "editTransactions", ["owner", "admin"]);
  }, [permissions, currentUserRole, isPrimaryOwner]);

  // Check if user can view transaction history based on permissions
  const canViewTransactionHistory = useCallback((): boolean => {
    return hasPermission(
      isPrimaryOwner,
      permissions,
      currentUserRole,
      "viewTransactionHistory",
      ["owner", "admin"]
    );
  }, [permissions, currentUserRole, isPrimaryOwner]);

  const canAddCurrency = useCallback((): boolean => {
    return hasPermission(isPrimaryOwner, permissions, currentUserRole, "addCurrency", ["owner", "admin"]);
  }, [permissions, currentUserRole, isPrimaryOwner]);

  const canChangeDefaultCurrency = useCallback((): boolean => {
    return hasPermission(isPrimaryOwner, permissions, currentUserRole, "changeDefaultCurrency", ["owner", "admin"]);
  }, [permissions, currentUserRole, isPrimaryOwner]);

  const canChangeCurrencyRateDecimalPlaces = useCallback((): boolean => {
    return hasPermission(
      isPrimaryOwner,
      permissions,
      currentUserRole,
      "changeCurrencyRateDecimalPlaces",
      ["owner", "admin"]
    );
  }, [permissions, currentUserRole, isPrimaryOwner]);

  const canEditPreferences = useCallback((): boolean => {
    return hasPermission(isPrimaryOwner, permissions, currentUserRole, "editPreferences", ["owner", "admin"]);
  }, [permissions, currentUserRole, isPrimaryOwner]);

  return {
    canDeleteTransactions,
    canEditTransactions,
    canViewTransactionHistory,
    canAddCurrency,
    canChangeDefaultCurrency,
    canChangeCurrencyRateDecimalPlaces,
    canEditPreferences,
  };
};
